import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Link from 'components/Link';
import FontIcon from 'components/FontIcon';
import removeAnimationClass from 'utils/Animations';
import classNames from 'classnames';
import classes from './SiteNavBDesktopLevelC.module.scss';

const SiteNavBDesktopLevelC = forwardRef((props, ref) => {
  const {
    heading,
    href,
    target,
    sections = [],
    show,
    animate,
    onAnimationEnd,
  } = props;

  const getContainerClass = () => {
    if (animate) {
      return show ? classes.containerDown : classes.containerUp;
    }

    return show ? classes.containerStayDown : classes.containerStayUp;
  };

  const renderLinks = (item, itemIndex) => {
    const {
      heading: treeItemHeading,
      href: treeItemHref,
      target: treeItemTarget,
      items: treeItemChildren = [],
    } = item;

    return (
      <div className={classes.treeItemContainer} key={`link-${itemIndex}`}>
        {href &&
          <Link
            href={treeItemHref}
            target={treeItemTarget}
            dangerouslySetInnerHTML={{ __html: treeItemHeading }}
          />}
        <div className={classes.childItemsContainer}>
          {treeItemChildren.map(({
            heading: childHeading,
            href: childHref,
            target: childTarget,
          }, childIndex) => (
            <div
              className={classes.childLinkContainer}
              key={`childLink-${childIndex}`}
            >
              <Link
                href={childHref}
                target={childTarget}
                dangerouslySetInnerHTML={{ __html: childHeading }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSection = (sectionHref, sectionHeading, sectionTarget, index, columns) => (
    <div key={`section-${index}`} className={classNames(classes.section, classes[`columns_${columns.length}`])}>
      <div className={classes.sectionHeading}>
        {sectionHref &&
          <Link
            className={classes.sectionHeadingLink}
            href={sectionHref}
            target={sectionTarget}
          >
            <h4
              dangerouslySetInnerHTML={{ __html: sectionHeading }}
            />
            <FontIcon icon='fas fa-chevron-right' className={classes.icon} />
          </Link>}
      </div>
      <div>
        {columns.map((column = [], columnIndex) => (
          <div
            key={columnIndex}
            className={classNames(classes.column, classes[`columns_${columns.length}`])}
          >
            {column.map((item, itemIndex) => renderLinks(item, itemIndex))}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div
      aria-label='desktop-menu'
      className={getContainerClass()}
      ref={ref}
      onAnimationEnd={() => {
        // need to add the "non-animation" class so that there is
        // no animation on window resize when it enters a different viewport
        removeAnimationClass(
          ref,
          classes.containerDown,
          classes.containerStayDown,
          classes.containerUp,
          classes.containerStayUp,
        );

        onAnimationEnd();
      }}
    >
      <div className={classes.wrapper}>
        <div className={classes.menuHeading}>
          {href &&
            <Link
              href={href}
              target={target}
              key={href}
              className={classes.menuHeadingLink}
            >
              <div
                className={classes.menuHeadingTitle}
                dangerouslySetInnerHTML={{ __html: heading }}
              />
              <FontIcon icon='far fa-chevron-right' className={classes.menuIcon} />
            </Link>}
        </div>
        <div className={classes.sections}>
          {sections.map((
            {
              href: sectionHref,
              heading: sectionHeading,
              target: sectionTarget,
              columns,
            }, index,
          ) => renderSection(sectionHref, sectionHeading, sectionTarget, index, columns))}
        </div>
      </div>
    </div>
  );
});

const TItem = PropTypes.shape({
  heading: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  items: PropTypes.array,
});

const TColumn = PropTypes.arrayOf(
  PropTypes.shape({
    items: PropTypes.arrayOf(TItem),
  }),
);

const TSection = PropTypes.shape({
  heading: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  columns: PropTypes.arrayOf(TColumn),
});

SiteNavBDesktopLevelC.propTypes = {
  href: PropTypes.string,
  heading: PropTypes.string,
  target: PropTypes.string,
  sections: PropTypes.arrayOf(TSection),
  onAnimationEnd: PropTypes.func,
  show: PropTypes.bool,
  animate: PropTypes.bool,
};

export default SiteNavBDesktopLevelC;
